import "./style.scss";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { Avatar, Collapse, Skeleton, Tooltip } from "@mui/material";
import DoubleArrow from "assets/img/svg/doubleArrow";
import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import ChipsActivatedMode from "components/shared/ChipsActivatedMode";
import PeriodName from "components/shared/PeriodName";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import TextButton from "components/shared/TextButton";
import TitleSeparation from "components/shared/TitleSeparation";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import type { IChannel } from "containers/Channels/types";
import type { ChannelParams } from "containers/TermFormContainer";
import type { GroupParams } from "containers/TermFormContainer";
import type { TFormCustomField } from "components/TitleFormContainer/types";
import type { ITitle } from "containers/Titles/types";
import { titlesFromCoveredIds } from "containers/TermFormContainer/CoveredTitlesField/titleDisplay";
import type { IWork, IWorkTitleSummary } from "containers/Works/types";
import { coverUrlForWork } from "containers/Works/workPreview";
import { isBookPublisher as isBookPublisherCompany } from "helpers/publisher";
import { formatLocaleDateString, formatNumber, replaceComma } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { TermGroup } from "store/beneficiaryStore";
import type { ITerm } from "store/managementStore";
import type { TPeriod } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";

import variables from "../../assets/styles/_variables.module.scss";
import AdvanceTotalBlock from "./AdvanceTotalBlock";
import BalanceTable from "./BalanceTable";
import ChannelsRecap from "./ChannelsRecap";
import CoveredTitlesRecap from "./CoveredTitlesRecap";
import { groupChannelFormulas } from "./formatTypeRecap";
import GroupRecap from "./GroupRecap";
import { termFormCustomFieldEntries, termSavedCustomFieldEntries } from "./termFormCustomFieldEntries";
import { useAdvanceRecapData } from "./useAdvanceRecapData";

interface IData {
    title: ITitle | null;
    work?: IWork | null;
    beneficiary: IBeneficiar | null;
    representativeId: number | null;
    channels: any;
    initialAdvance: number | string;
    advancePayment: number;
    reference: string | null;
    endDate: Date | null;
    initialProvision: number | undefined;
    compensate: number;
    representative: IRepresentative | null;
    cumulativeRights: number;
    customFields?: TFormCustomField[];
}

interface ITermRecap {
    /**
     * Data from the form.
     */
    data: Omit<IData, "representativeId" | "channels">;
    /**
     * Array with all channels chosen in this form.
     */
    chosenChannels: ChannelParams[];
    /**
     * All existing channels for this company.
     */
    channels: IChannel[];
    chosenCompensation?: number | string | null;
    /**
     * Whether company uses groups of channels for compensation.
     */
    usesGroups?: boolean;
    chosenGroups?: GroupParams[];
    channelsInGroups: ChannelParams[];
    /**
     * Whether company uses groups of terms for advance compensation.
     */
    usesAdvanceCompensation?: boolean;
    /** Advance group chosen */
    chosenAdvanceGroup?: TermGroup | undefined | null;
    /**
     * Whether to display the buttons "See titles" (yes when on Show Detail component, no inside form)
     */
    showAdvanceGroupTitles?: boolean;
    settingsStepData?: Partial<{
        reference: string | undefined;
        contractTypology?: string | null;
        initialAdvance: string | undefined;
        initialProvision: string | undefined;
        endDate: Date | null | undefined;
        cumulativeRights: string | undefined;
        isTitlePublishedBeforePeriod?: number | null;
        titleReleaseDate?: string | Date | null;
        currentPeriod?: TPeriod;
    }>;
    /**
     * (When creating new term) Determines whether they want to create a payment in Advance Management
     */
    createAdvanceInManagement?: boolean;
    /**
     * (For edit mode) Boolean that determines if a corresponding payment exists in Advance Management
     */
    advancePaymentExists?: boolean;
    /**
     * Does the company want to display initial advance (handles chip for linked advance payment)
     */
    usesInitialAdvance: boolean;
    /**
     * Whether the company uses cumulative royalties (controls visibility of the Droits cumulés line).
     */
    usesCumulativeRoyalties?: boolean;
    /**
     * Inline advance payment slices entered in the term form (amounts in cents).
     * Shown as a minimal table in the recap when present.
     */
    inlineAdvancePayment?: TAdvancePayment | null;
    /**
     * Is recap accessed from edit form or new.
     */
    mode: "new" | "edit";
    coveredTitles?: IWorkTitleSummary[];
    coveredTitleIds?: number[];
    /**
     * Is recap accessed from form or details drawer.
     */
    from: "form" | "drawer";
    term?: ITerm;
    termAdvancePayments?: IAdvanceSlice[];
}

/**
 * This component renders all the information entered from the Term form.
 */
const TermRecap = ({
    data,
    chosenChannels,
    channels,
    chosenCompensation,
    usesGroups,
    chosenGroups,
    channelsInGroups,
    usesAdvanceCompensation,
    chosenAdvanceGroup,
    showAdvanceGroupTitles = false,
    settingsStepData,
    createAdvanceInManagement,
    advancePaymentExists,
    usesInitialAdvance,
    mode,
    from,
    term,
    termAdvancePayments,
    usesCumulativeRoyalties,
    inlineAdvancePayment,
    coveredTitles,
    coveredTitleIds,
}: ITermRecap) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { currencySymbol, companyInfos, usesCegidGect } = useCompanySettings();
    const { items: titleFormatTypes, status: titleFormatTypesStatus } = useAppSelector(
        (state) => state.titleFormatTypes,
    );
    const isBookPublisher = isBookPublisherCompany(companyInfos?.publisherType);

    const { drawerAdvanceSlices, inlineAdvanceTotal, drawerAdvanceTotal, handleGoToAdvancePayment } =
        useAdvanceRecapData({
            from,
            term,
            termAdvancePayments,
            inlineAdvancePayment,
            titleEan13: data.title?.ean13,
            advancePaymentId: term?.advancePaymentId,
        });

    const { periods } = useAppSelector((state) => state.period);
    const { work, beneficiary, advancePayment, representative, compensate } = data;
    const resolvedCoveredTitles = useMemo(
        () => coveredTitles ?? titlesFromCoveredIds(coveredTitleIds ?? term?.coveredTitleIds, work?.titles ?? []),
        [coveredTitles, coveredTitleIds, term?.coveredTitleIds, work?.titles],
    );

    useEffect(() => {
        if (!isBookPublisher || titleFormatTypesStatus !== "idle") return;
        dispatch(fetchTitleFormatTypes());
    }, [dispatch, isBookPublisher, titleFormatTypesStatus]);

    const formatTypeNamesById = useMemo(() => {
        const byId = new Map<number, string>();
        for (const item of titleFormatTypes) {
            byId.set(item.id, item.name);
        }
        for (const title of work?.titles ?? []) {
            if (title.formatTypeId == null) continue;
            const name = title.formatTypeName?.trim();
            if (name) byId.set(title.formatTypeId, name);
        }
        return byId;
    }, [titleFormatTypes, work?.titles]);
    const channelById = useMemo(() => new Map(channels.map((item) => [item.id, item])), [channels]);
    const standaloneFormulas = useMemo(() => groupChannelFormulas(chosenChannels ?? []), [chosenChannels]);
    const workCoverUrl = coverUrlForWork(work);
    const {
        reference,
        contractTypology,
        initialAdvance,
        initialProvision,
        endDate,
        cumulativeRights,
        isTitlePublishedBeforePeriod,
        titleReleaseDate,
        currentPeriod,
    } = settingsStepData ?? {
        reference: data.reference,
        contractTypology: term?.contractTypology,
        initialAdvance: data.initialAdvance,
        initialProvision: data.initialProvision,
        endDate: data.endDate,
        cumulativeRights: data.cumulativeRights,
        isTitlePublishedBeforePeriod: term?.isTitlePublishedBeforePeriod ?? 1,
        titleReleaseDate: data.title?.releaseDate ?? null,
        currentPeriod: undefined,
    };

    const redditionPeriod = useMemo(() => {
        if (currentPeriod) return currentPeriod;
        if (term?.originalPeriod == null) return undefined;

        const periodId = +term.originalPeriod;
        return periods.find((item) => item.id === periodId);
    }, [currentPeriod, term?.originalPeriod, periods]);

    const workStatus = useAppSelector((state) => state.works.selectedWorkStatus);
    const beneficiaryStatus = useAppSelector((state) => state.beneficiary.beneficiaryStatus);

    const advance = typeof advancePayment === "string" ? replaceComma(advancePayment) : advancePayment;
    const provision = typeof initialProvision === "string" ? replaceComma(initialProvision) : initialProvision ?? 0;
    const firstAdvance = typeof initialAdvance === "string" ? replaceComma(initialAdvance) : initialAdvance ?? 0;
    const sumRights = typeof cumulativeRights === "string" ? replaceComma(cumulativeRights) : cumulativeRights ?? 0;

    // Determines whether collapse is open or not.
    const [openTitles, setOpenTitles] = useState(false);

    // Sometimes the advance group titles[] has null inside. This filters it, otherwise it counts length as 1.
    const associatedTitles =
        chosenAdvanceGroup?.titles !== undefined
            ? chosenAdvanceGroup?.titles.filter((title) => title !== null)
            : chosenAdvanceGroup?.titles;

    const toggleTitlesList = (e: any) => {
        e.stopPropagation();
        setOpenTitles((prev) => !prev);
    };

    const hasAdvanceRecapContent =
        (createAdvanceInManagement && !!inlineAdvancePayment?.slices?.length && inlineAdvanceTotal !== null) ||
        (from === "drawer" && !!drawerAdvanceSlices?.length && drawerAdvanceTotal !== null);

    const showAdvanceRecapSection = hasAdvanceRecapContent;

    const hasChannelsRecapContent =
        (chosenChannels?.length ?? 0) > 0 || (!!usesGroups && (chosenGroups?.length ?? 0) > 0);

    const showChannelsRecapSection = from !== "drawer" || hasChannelsRecapContent;

    const beneficiaryFullName = [beneficiary?.name, beneficiary?.surname].filter(Boolean).join(" ");
    const { customFields } = useAppSelector((state) => state.customFields);
    const termCustomFieldLines = useMemo(() => {
        const termFieldTypes = customFields.filter((field) => field.relatedResource === "term");
        if (from === "form") {
            return termFormCustomFieldEntries(termFieldTypes, data.customFields, mode);
        }
        return termSavedCustomFieldEntries(termFieldTypes, term?.customFields);
    }, [customFields, data.customFields, from, mode, term]);

    const customFieldsFor = (location: string) =>
        termCustomFieldLines.filter((field) => field.formLocation === location);

    const renderCustomFields = (location: string) =>
        customFieldsFor(location).map((field) => (
            <span className="grey" key={field.uuid}>
                {field.name} <span className="option-text">{field.value || "-"}</span>
            </span>
        ));

    return (
        <>
            <div className="main-info-recap">
                {from !== "drawer" && (
                    <div className="contract-title">
                        {!work?.name && workStatus == "pending" ? (
                            <Skeleton width={"14rem"} />
                        ) : (
                            <div className="left">
                                <Avatar
                                    src={workCoverUrl}
                                    variant="rounded"
                                    className="term-recap-avatar"
                                    sx={{
                                        width: "30px",
                                        height: "45px",
                                        bgcolor: "#EEF3FD",
                                        border: "1px solid #EAEAEA",
                                    }}
                                >
                                    {!workCoverUrl && work?.name?.slice(0, 1).toUpperCase()}
                                </Avatar>
                                <Tooltip title={work?.name ?? ""} disableHoverListener={from !== "form"}>
                                    <h2 id="recap-title-name" className={`${from === "form" ? "ellipsis" : ""}`}>
                                        {work?.name ?? ""}
                                    </h2>
                                </Tooltip>
                            </div>
                        )}

                        <DoubleArrow color={variables["foreground"]} />
                        {beneficiaryStatus == "pending" ? (
                            <Skeleton width={"14rem"} />
                        ) : (
                            <div className="right">
                                <Avatar
                                    src={beneficiary?.avatarUrl}
                                    className="term-recap-avatar"
                                    sx={{
                                        width: "30px",
                                        height: "30px",
                                        bgcolor: variables["beneficiary-avatar-background"],
                                        color: variables["beneficiary-avatar-letter"],
                                        border: "1px solid #EAEAEA",
                                    }}
                                    imgProps={{ sx: { objectFit: "contain" } }}
                                >
                                    {!beneficiary?.avatarUrl &&
                                        `${beneficiary?.name?.slice(0, 1).toUpperCase() ?? ""}${beneficiary?.surname
                                            ?.slice(0, 1)
                                            .toUpperCase()}`}
                                </Avatar>
                                <h2
                                    id="recap-beneficiary-name"
                                    className={from === "form" ? "ellipsis" : undefined}
                                    title={from === "form" && beneficiaryFullName ? beneficiaryFullName : undefined}
                                >
                                    {beneficiaryFullName}
                                </h2>
                            </div>
                        )}
                    </div>
                )}
                {isBookPublisher && resolvedCoveredTitles.length > 0 && (
                    <CoveredTitlesRecap titles={resolvedCoveredTitles} />
                )}
                <TitleSeparation title={t("ConnectTitle.SettingsStep.Title1")} titleStyle={{ fontSize: "0.9rem" }} />
                {representative && (
                    <div style={{ display: "flex", alignItems: "center" } as React.CSSProperties}>
                        <span className="grey" style={{ marginRight: "0.5rem" }}>
                            {t("TermRecap.Representee")}{" "}
                            <span className="option-text">
                                {(representative?.surname ?? "").toUpperCase() ?? "-"} {representative?.name ?? ""}
                            </span>
                        </span>
                        {!!representative?.computeFromRepresentative && (
                            <Tooltip title={t("Representatives.Collapse.DiffProfileTooltip")}>
                                <div className="associated-person-info">
                                    <ErrorOutlineIcon /> {t("Representatives.CalculationBase")}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}
                <span className="grey">
                    {t("TermRecap.ContractReference")}{" "}
                    <span className="option-text" id="recap-reference">
                        {!reference || reference === "" ? "-" : reference}
                    </span>
                </span>
                {renderCustomFields("parties")}
                {usesCegidGect && (
                    <span className="grey">
                        {t("TermRecap.ContractTypology")}{" "}
                        <span className="option-text" id="recap-contract-typology">
                            {contractTypology || "-"}
                        </span>
                    </span>
                )}
                <span className="grey">
                    {t("TermRecap.EndDate")}{" "}
                    <span className="option-text" id="recap-end-date">
                        {endDate ? formatLocaleDateString({ date: endDate }) : "-"}
                    </span>
                </span>
                <span className="grey">
                    {t("TermRecap.TitleReleaseDate")}{" "}
                    <span className="option-text" id="recap-title-release-date">
                        {titleReleaseDate ? formatLocaleDateString({ date: titleReleaseDate }) : "-"}
                    </span>
                </span>
                {redditionPeriod && (
                    <div className="grey recap-period-line">
                        <span>{t("ConnectTitle.SettingsStep.PeriodLabel")} :</span>
                        <div className="option-text">
                            <PeriodName
                                period={redditionPeriod}
                                variant="termForm"
                                textStyle={{ fontSize: "0.87rem", fontWeight: 400 }}
                            />
                        </div>
                    </div>
                )}
                <span className="grey">
                    {t("ConnectTitle.SettingsStep.TitlePublishedBeforePeriod")}{" "}
                    <span className="option-text">
                        {isTitlePublishedBeforePeriod == 1 ? t("ConnectTitle.Yes") : t("ConnectTitle.No")}
                    </span>
                </span>
            </div>

            {isTitlePublishedBeforePeriod == 1 && (
                <div className="initial-data-recap">
                    <TitleSeparation
                        title={t("ConnectTitle.SettingsStep.Title2")}
                        titleStyle={{ fontSize: "0.9rem" }}
                    />
                    {usesInitialAdvance && (
                        <span
                            className="grey"
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "space-between" }}
                        >
                            <div>
                                {t("TermRecap.InitialAdvance")}{" "}
                                <span className="option-text" id="recap-advance-payment">
                                    {initialAdvance?.toString() === ""
                                        ? "0.00 €"
                                        : formatNumber(firstAdvance, {
                                              isCurrency: true,
                                              always2Digit: true,
                                          }) ?? "0"}
                                </span>
                            </div>
                            {(createAdvanceInManagement || advancePaymentExists) && (
                                <ChipsActivatedMode
                                    text={
                                        mode === "new"
                                            ? t("TermRecap.CreateAdvancePayment")
                                            : t("TermRecap.AdvanceExistsAsPayment")
                                    }
                                    chipColor={variables.info}
                                    textColor={variables.info}
                                    backgroundColor={variables["info-light"]}
                                />
                            )}
                        </span>
                    )}
                    {usesCumulativeRoyalties && (
                        <span className="grey">
                            {t("TermRecap.CumulativeRights")}{" "}
                            <span className="option-text" id="recap-initial-provision">
                                {cumulativeRights?.toString() === ""
                                    ? "0.00 €"
                                    : formatNumber(sumRights, {
                                          isCurrency: true,
                                          always2Digit: true,
                                      }) ?? "0"}
                            </span>
                        </span>
                    )}
                    <span className="grey">
                        {t("TermRecap.Provision")}{" "}
                        <span className="option-text" id="recap-initial-provision">
                            {initialProvision?.toString() === ""
                                ? `0.00 ${currencySymbol}`
                                : formatNumber(provision, {
                                      isCurrency: true,
                                      always2Digit: true,
                                  }) ?? "0"}
                        </span>
                    </span>
                    <span className="grey">
                        {t("TermRecap.Advance")}{" "}
                        <span className="option-text" id="recap-advance-payment">
                            {advancePayment?.toString() === ""
                                ? `0.00 ${currencySymbol}`
                                : formatNumber(advance, {
                                      isCurrency: true,
                                      always2Digit: true,
                                  }) ?? "0"}
                        </span>
                    </span>
                </div>
            )}

            {from === "drawer" && <BalanceTable term={term} termAdvancePayments={termAdvancePayments} />}

            {showAdvanceRecapSection && (
                <div className="advance-payment-recap">
                    <TitleSeparation
                        title={t("ConnectTitle.SettingsStep.TitleAdvancePayment")}
                        titleStyle={{ fontSize: "0.9rem" }}
                    />
                    <AdvanceTotalBlock
                        from={from}
                        mode={mode}
                        createAdvanceInManagement={createAdvanceInManagement}
                        usesInitialAdvance={usesInitialAdvance}
                        inlineAdvancePayment={inlineAdvancePayment}
                        inlineAdvanceTotal={inlineAdvanceTotal}
                        drawerAdvanceSlices={drawerAdvanceSlices}
                        drawerAdvanceTotal={drawerAdvanceTotal}
                        onGoToAdvancePayment={handleGoToAdvancePayment}
                    />
                    {!usesInitialAdvance && mode === "new" && createAdvanceInManagement && (
                        <span style={{ fontSize: "12px", color: variables.info }}>
                            {t("TermRecap.CreateAdvancePayment")}
                        </span>
                    )}
                </div>
            )}

            <div className="balance-settings-recap">
                <TitleSeparation title={t("ConnectTitle.SettingsStep.Title3")} titleStyle={{ fontSize: "0.9rem" }} />
                <span className="grey">
                    {t("ConnectTitle.Compensate")}{" "}
                    <span className="option-text" id="recap-compensate">
                        {+`${chosenCompensation || compensate}` === 0 ? t("ConnectTitle.No") : t("ConnectTitle.Yes")}
                    </span>
                </span>
                {usesAdvanceCompensation && (
                    <>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <span className="grey">
                                {t("CompensationGroups.AdvanceGroupsQuestion")}{" "}
                                <span className="option-text" id="recap-advance-compensation">
                                    {chosenAdvanceGroup ? t("ConnectTitle.Yes") : t("ConnectTitle.No")}
                                </span>
                            </span>
                            {chosenAdvanceGroup && (
                                <div
                                    className="advance-group-details"
                                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                >
                                    <div className="group-name">
                                        <LibraryBooksIcon />
                                        <span className="grey">
                                            {t("CompensationGroups.AdvanceGroupName")}{" "}
                                            <span className="option-text">{chosenAdvanceGroup?.name ?? "-"}</span>
                                        </span>
                                    </div>
                                    {showAdvanceGroupTitles && (
                                        <TextButton
                                            text={t("CompensationGroups.SeeTitles")}
                                            handleClick={(e: any) => toggleTitlesList(e)}
                                            style={{ fontSize: "12px" }}
                                            underlineOnHover
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        <Collapse in={openTitles} timeout="auto" unmountOnExit>
                            <ScrollShadowWrapper className="group-list--collapse">
                                <div className="titles-list">
                                    {associatedTitles &&
                                        associatedTitles.map((title: string, i: number) => (
                                            <p className="title-name" key={`${i}-title`}>
                                                {title ?? ""}
                                            </p>
                                        ))}
                                </div>
                            </ScrollShadowWrapper>
                        </Collapse>
                    </>
                )}
                {renderCustomFields("settings")}
            </div>

            {(showChannelsRecapSection || customFieldsFor("channels").length > 0) && (
                <div className="channels-recap">
                    <TitleSeparation title={t("TermRecap.Channels")} titleStyle={{ fontSize: "0.9rem" }} />
                    {chosenChannels?.length > 0 &&
                        [...standaloneFormulas.entries()].map(([channelId, formulas]) => {
                            const currentChannel = channelById.get(channelId);
                            return (
                                <ChannelsRecap
                                    key={channelId}
                                    fullChannelData={currentChannel}
                                    channel={formulas.defaultRow}
                                    formatOverrides={formulas.overrides}
                                    formatTypeNamesById={formatTypeNamesById}
                                />
                            );
                        })}
                    {usesGroups &&
                        chosenGroups &&
                        chosenGroups.map((group: GroupParams) => {
                            const groupChannels = channelsInGroups?.filter((channel) => channel.groupId === group.id);
                            const groupChannelsNames = groupChannels
                                ?.filter((channel) => channel.formatTypeId == null)
                                .map((channel) => channel.channelName);

                            return (
                                <GroupRecap
                                    key={group.id}
                                    group={group}
                                    groupChannels={groupChannels}
                                    groupChannelsNames={groupChannelsNames}
                                    channels={channels}
                                    formatTypeNamesById={formatTypeNamesById}
                                />
                            );
                        })}
                    {renderCustomFields("channels")}
                </div>
            )}
        </>
    );
};

export default TermRecap;
